import { useState, useEffect, useCallback, useRef } from 'react';
import { hitlApi } from '@/api';
// S4-35: текст ошибки берётся из i18n в момент сбоя (не при загрузке модуля).
import i18n from '@/i18n';
import type {
  ApprovalRequest,
  ApprovalRequestDetail,
  ApprovalStats,
  ApprovalResult,
  ApproveAction,
  RejectAction,
} from '@/types';

/**
 * S5-26 р.2 F1/F4: исход решения по карточке, видимый юзеру.
 * - `error` — решение НЕ записано (success:false / HTTP-ошибка): карточка остаётся, повтор осмыслен;
 * - `notExecuted` — решение записано, действие не исполнено (success:true ∧ executed:false,
 *   кроме no_handler): карточка уходит (на сервере она уже не PENDING), пояснение остаётся.
 * Хранится по approvalId — исход одной карточки не стирается решением по другой.
 */
export interface DecisionNotice {
  approvalId: string;
  kind: 'error' | 'notExecuted';
  message: string;
  /** actionType карточки — чтобы баннер назвал, к какому запросу относится */
  actionType?: string;
}

/**
 * Hook для работы с pending approvals.
 *
 * N18 §14 #5 (2026-06-07): optional `pollIntervalMs` для poll-based refresh.
 *   Сайдбар-badge передаёт 30000ms чтобы count оставался свежим между
 *   навигациями страниц без full page reload. ApprovalsPage обычно вызывает
 *   без интервала — actions (approve/reject) обновляют local state сами.
 *
 * @param options.pollIntervalMs — если задан, useEffect setInterval'ом
 *   перетягивает список каждые N миллисекунд (≥ 5000ms enforce'ится).
 *   Pause при tab-hidden (visibilitychange) чтобы не жечь API в фоне.
 */
export function useApprovals(options: { pollIntervalMs?: number } = {}) {
  const { pollIntervalMs } = options;
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // S5-26 F3 + р.2 F4: исходы решений по карточкам — отдельно от error загрузки (тот заменяет весь список),
  // по approvalId: успех по карточке B не стирает сбой по карточке A
  const [actionNotices, setActionNotices] = useState<Record<string, DecisionNotice>>({});
  // actionType карточки на момент решения (карточка может уже уйти из списка)
  const approvalsRef = useRef<ApprovalRequest[]>(approvals);
  useEffect(() => {
    approvalsRef.current = approvals;
  }, [approvals]);

  const fetchApprovals = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await hitlApi.getPendingApprovals();
      setApprovals(data);
    } catch (err) {
      setError(i18n.t('approvals.errors.loadPending'));
      console.error('Failed to fetch approvals:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  // N18 §14 #5: optional polling для badge live-update.
  useEffect(() => {
    if (!pollIntervalMs || pollIntervalMs <= 0) return;
    // Защита от слишком частого poll (server load) — минимум 5 сек.
    const safeInterval = Math.max(pollIntervalMs, 5000);

    const tick = () => {
      // Skip refresh когда вкладка hidden — экономия API + battery.
      if (document.visibilityState !== 'visible') return;
      void hitlApi
        .getPendingApprovals()
        .then((data) => setApprovals(data))
        .catch((err) => {
          // Тихая ошибка для polling — initial fetch handler уже показал error.
          console.warn('[useApprovals] poll refresh failed:', err);
        });
    };

    const handle = window.setInterval(tick, safeInterval);
    // Immediate refresh когда вкладка становится visible (catch-up после long hide).
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pollIntervalMs]);

  /** Записать исход решения по карточке (заменяет прежний исход ЭТОЙ карточки, чужие не трогает). */
  const setNotice = useCallback((approvalId: string, kind: DecisionNotice['kind'], message: string) => {
    const actionType = approvalsRef.current.find((a) => a.id === approvalId)?.actionType;
    setActionNotices((prev) => ({ ...prev, [approvalId]: { approvalId, kind, message, actionType } }));
  }, []);

  /** Снять исход решения по карточке. */
  const dismissActionNotice = useCallback((approvalId: string) => {
    setActionNotices((prev) => {
      if (!(approvalId in prev)) return prev;
      const next = { ...prev };
      delete next[approvalId];
      return next;
    });
  }, []);

  /**
   * S5-26 F3 + р.2 F1/F4: применить ответ approve/reject к списку.
   * @contract Исход решения ровно один и правдив
   * @requires result — ответ BFF на approve/reject этой карточки
   * @ensures success:false → карточка ОСТАЁТСЯ, notice `error` (approvals.errors.actionFailed)
   * @ensures success:true ∧ executed:false (кроме no_handler) → карточка УХОДИТ (решение уже записано
   *   в Master Agent — повтор дал бы 400), notice `notExecuted`: fact_not_found →
   *   approvals.errors.factAlreadyDeleted, прочие → approvals.errors.decisionRecordedNotExecuted
   * @ensures success:true ∧ (executed:true ∨ no_handler ∨ нет executionResult) → карточка уходит,
   *   прежний notice ЭТОЙ карточки снят; notice других карточек не трогается
   * @modifies approvals, actionNotices
   */
  const applyDecisionResult = useCallback(
    (approvalId: string, result: ApprovalResult) => {
      if (result.success === false) {
        setNotice(approvalId, 'error', i18n.t('approvals.errors.actionFailed'));
        console.warn('[useApprovals] decision not recorded:', approvalId, result);
        return;
      }

      const exec = result.executionResult;
      // no_handler = для actionType нет исполнителя по дизайну (настройки применены сразу) — не сбой
      if (exec?.executed === false && exec.reason !== 'no_handler') {
        setNotice(
          approvalId,
          'notExecuted',
          i18n.t(
            exec.reason === 'fact_not_found'
              ? 'approvals.errors.factAlreadyDeleted'
              : 'approvals.errors.decisionRecordedNotExecuted',
          ),
        );
        console.warn('[useApprovals] decision recorded, action not executed:', approvalId, result);
      } else {
        dismissActionNotice(approvalId);
      }
      setApprovals((prev) => prev.filter((a) => a.id !== approvalId));
    },
    [setNotice, dismissActionNotice],
  );

  // Подтвердить действие
  const approve = useCallback(
    async (approvalId: string, data?: ApproveAction) => {
      try {
        const result = await hitlApi.approveAction(approvalId, data);
        applyDecisionResult(approvalId, result);
        return result;
      } catch (err) {
        console.error('Failed to approve action:', err);
        // HTTP-ошибка (в т.ч. 404 чужой карточки) — решение не записано, карточка остаётся
        setNotice(approvalId, 'error', i18n.t('approvals.errors.actionFailed'));
        throw err;
      }
    },
    [applyDecisionResult, setNotice],
  );

  // Отклонить действие
  const reject = useCallback(
    async (approvalId: string, data?: RejectAction) => {
      try {
        const result = await hitlApi.rejectAction(approvalId, data);
        applyDecisionResult(approvalId, result);
        return result;
      } catch (err) {
        console.error('Failed to reject action:', err);
        // HTTP-ошибка (в т.ч. 404 чужой карточки) — решение не записано, карточка остаётся
        setNotice(approvalId, 'error', i18n.t('approvals.errors.actionFailed'));
        throw err;
      }
    },
    [applyDecisionResult, setNotice],
  );

  return {
    approvals,
    isLoading,
    error,
    /** S5-26 р.2 F4: исходы решений по карточкам (по одному на approvalId) */
    actionNotices: Object.values(actionNotices),
    dismissActionNotice,
    refetch: fetchApprovals,
    approve,
    reject,
    pendingCount: approvals.length,
  };
}

/**
 * Hook для получения деталей approval
 */
export function useApprovalDetails(approvalId: string | null) {
  const [approval, setApproval] = useState<ApprovalRequestDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = useCallback(async () => {
    if (!approvalId) {
      setApproval(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await hitlApi.getApprovalDetails(approvalId);
      setApproval(data);
    } catch (err) {
      setError(i18n.t('approvals.errors.loadDetails'));
      console.error('Failed to fetch approval details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [approvalId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  return { approval, isLoading, error, refetch: fetchDetails };
}

/**
 * UX-HITL-1 part 2 (2026-04-29): Hook для drill-down списка approvals
 * по категории. Загружает по требованию (lazy) — fetchByCategory вызывается
 * только когда юзер кликает на категорию.
 *
 * Включает already-decided запросы (approved/rejected/expired), которые
 * не возвращаются в `getPendingApprovals`.
 */
export function useApprovalsByCategory() {
  const [approvalsByCategory, setApprovalsByCategory] = useState<Record<string, ApprovalRequest[]>>(
    {},
  );
  const [loadingCategory, setLoadingCategory] = useState<string | null>(null);
  const [errorByCategory, setErrorByCategory] = useState<Record<string, string | null>>({});

  const fetchByCategory = useCallback(async (category: string) => {
    setLoadingCategory(category);
    setErrorByCategory((prev) => ({ ...prev, [category]: null }));

    try {
      const data = await hitlApi.getApprovalsByCategory(category);
      setApprovalsByCategory((prev) => ({ ...prev, [category]: data }));
    } catch (err) {
      console.error(`Failed to fetch approvals for category ${category}:`, err);
      setErrorByCategory((prev) => ({
        ...prev,
        [category]: i18n.t('approvals.category.loadError'),
      }));
    } finally {
      setLoadingCategory(null);
    }
  }, []);

  return {
    approvalsByCategory,
    loadingCategory,
    errorByCategory,
    fetchByCategory,
  };
}

/**
 * Hook для статистики approvals
 */
export function useApprovalStats(startDate?: string, endDate?: string) {
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await hitlApi.getApprovalStats(startDate, endDate);
      setStats(data);
    } catch (err) {
      setError(i18n.t('approvals.errors.loadStats'));
      console.error('Failed to fetch approval stats:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refetch: fetchStats };
}
